// QR de la etiqueta de Mercado Libre que imprimió la tienda, en la columna QR
// de Lista de envíos. Lo guarda el servidor (labelQrService: la tarea lo baja
// de ML mientras el envío no salió, o queda al escanear la etiqueta) y la
// página pide solo los de la página que se ve (GET /v2/deliveries/label-qr).
// Tocarlo lo abre grande para escanearlo desde la pantalla.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../ui/Button";
import { authFetch } from "../../lib/authFetch";

export type LabelQr = {
  delivery_id: number;
  payload: string;
  source: "ml_label" | "scan" | string;
  obtained_at: string | null;
  svg: string;
};

const SOURCE_TEXT: Record<string, string> = {
  ml_label: "Bajado de Mercado Libre",
  scan: "Leído al escanear la etiqueta",
};

const svgSrc = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const formatDate = (value: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Los QR de los envíos que se ven, de a 100 y una vez por envío hasta que la
// lista se vuelve a cargar (`reloadKey`): ahí se vuelven a pedir, por si la
// tarea guardó alguno nuevo.
export function useLabelQrs(ids: number[], token: string | null | undefined, reloadKey?: unknown) {
  const [qrs, setQrs] = useState<Record<number, LabelQr>>({});
  const asked = useRef<Set<number>>(new Set());
  const key = ids.join(",");

  useEffect(() => { asked.current.clear(); }, [reloadKey]);

  useEffect(() => {
    const missing = ids.filter((id) => !asked.current.has(id));
    if (!token || !missing.length) return;
    missing.forEach((id) => asked.current.add(id));
    // Lo que llega se suma aunque ya se haya cambiado de página: queda para
    // cuando se vuelva (cada QR va por su envío).
    (async () => {
      for (let i = 0; i < missing.length; i += 100) {
        const part = missing.slice(i, i + 100);
        try {
          const res = await authFetch(`/v2/deliveries/label-qr?ids=${part.join(",")}`, { token });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || json?.ok === false) throw new Error(json?.error || "No se pudieron traer los QR");
          const found: LabelQr[] = Array.isArray(json?.qrs) ? json.qrs : [];
          if (found.length) {
            setQrs((prev) => {
              const next = { ...prev };
              found.forEach((qr) => { next[qr.delivery_id] = qr; });
              return next;
            });
          }
        } catch {
          // Sin QR la columna queda como antes; se reintenta al volver a la página.
          part.forEach((id) => asked.current.delete(id));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token, reloadKey]);

  return qrs;
}

const boxStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: "40px", height: "40px", borderRadius: "12px",
  border: "1px solid var(--border)", background: "var(--surface-alt)", fontSize: "18px",
};

const qrButtonStyle: React.CSSProperties = {
  ...boxStyle, padding: "3px", background: "#fff", cursor: "zoom-in",
};

// Va por portal a <body> (como el diálogo de FeedbackButton): dentro de la
// tabla quedaba encerrado en la tarjeta y con su fondo translúcido.
const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000, padding: "16px",
};

const cardStyle: React.CSSProperties = {
  background: "var(--background)", border: "1px solid var(--border)", borderRadius: "20px",
  padding: "24px", maxWidth: "380px", width: "100%", textAlign: "center",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
};

const titleStyle: React.CSSProperties = { margin: "0 0 4px", fontSize: "18px", fontWeight: 800, color: "var(--text-primary)" };
const mutedStyle: React.CSSProperties = { fontSize: "13px", color: "var(--text-secondary)" };
const bigQrStyle: React.CSSProperties = { width: "260px", height: "260px", margin: "16px auto", display: "block", background: "#fff", padding: "8px", borderRadius: "12px" };
const payloadStyle: React.CSSProperties = {
  margin: "12px 0 0", padding: "8px 10px", borderRadius: "10px", background: "var(--surface-alt)",
  border: "1px solid var(--border)", fontFamily: "monospace", fontSize: "11px",
  color: "var(--text-secondary)", wordBreak: "break-all", textAlign: "left",
};
const actionsStyle: React.CSSProperties = { display: "flex", gap: "12px", marginTop: "18px", justifyContent: "center" };

export default function LabelQrCell({ qr, tracking }: { qr?: LabelQr; tracking?: string | null }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!qr) {
    return <div style={boxStyle} title="Todavía no hay QR de la etiqueta de Mercado Libre">⌘</div>;
  }

  const copy = async () => {
    try { await navigator.clipboard.writeText(qr.payload); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <>
      <button
        type="button"
        style={qrButtonStyle}
        title="QR de la etiqueta que imprimió la tienda (tocá para agrandar)"
        aria-label={`Ver el QR de la etiqueta del envío ${tracking ?? qr.delivery_id}`}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}
      >
        <img src={svgSrc(qr.svg)} alt="" width={34} height={34} style={{ display: "block" }} />
      </button>

      {open && createPortal(
        <div style={overlayStyle} onClick={(event) => { event.stopPropagation(); setOpen(false); }}>
          <div
            style={cardStyle}
            role="dialog"
            aria-modal="true"
            aria-label="QR de la etiqueta"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={titleStyle}>QR de la etiqueta</h3>
            <div style={mutedStyle}>Envío {tracking ?? qr.delivery_id}</div>
            <img src={svgSrc(qr.svg)} alt={`QR de la etiqueta del envío ${tracking ?? qr.delivery_id}`} style={bigQrStyle} />
            <div style={mutedStyle}>
              {SOURCE_TEXT[qr.source] ?? qr.source}
              {formatDate(qr.obtained_at) ? ` · ${formatDate(qr.obtained_at)}` : ""}
            </div>
            <div style={payloadStyle}>{qr.payload}</div>
            <div style={actionsStyle}>
              <Button variant="secondary" onClick={copy}>{copied ? "Copiado" : "Copiar texto"}</Button>
              <Button onClick={() => setOpen(false)}>Cerrar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
